import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "../../../component/commonCard/style.module.css";
import RankedList from "../../../component/commonCard/parts/RankedList";
import LeaderboardTable from "../../../component/commonCard/parts/LeaderboardTable";
import GeoMap from "../../../component/commonCard/parts/GeoMap";
import NotAvailableYet from "../NotAvailableYet";
import { COPPER, TEAL } from "../../../component/commonCard/parts/theme";
import { getGeoAnalytics } from "../../../api/HttpApis";
import { showToast } from "../../../utils/Toastify/Toastify";
import ExportButtons from "../ExportButtons";

// The map needs every country, not just the top 10, so the API is asked for
// up to LIST_LIMIT_FOR_MAP rows and the ranked lists are trimmed to TOP_N here.
const TOP_N = 10;
const LIST_LIMIT_FOR_MAP = 250;

const GeoSection = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const platform = searchParams.get("platform") || "";
  const contentType = searchParams.get("contentType") || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await getGeoAnalytics({ from, to, limit: LIST_LIMIT_FOR_MAP, platform, contentType });
        if (!cancelled) setData(res?.data?.data || null);
      } catch (err) {
        if (!cancelled) showToast(err?.response?.data?.message || "Failed to load geo analytics", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [from, to, platform, contentType]);

  if (loading) return <div className={styled.loading}>Loading geographic analytics…</div>;

  const bunnyCountries = data?.bunny?.available && data.bunny.country_view_counts
    ? Object.entries(data.bunny.country_view_counts).map(([country, views]) => ({ country, views }))
    : [];

  const exportRows = [
    ...(data?.top_countries || []).map((c) => ({ Metric: `Country: ${c.country}`, Value: c.views, "Share %": c.share_percent })),
    ...(data?.top_cities || []).map((c) => ({ Metric: `City: ${c.city}, ${c.country}`, Value: c.views, "Share %": c.share_percent })),
    ...bunnyCountries.map((c) => ({ Metric: `Bunny Country: ${c.country}`, Value: c.views, "Share %": "" })),
  ];

  return (
    <>
      <ExportButtons rows={exportRows} filename="geo-analytics" />

      <div className={styled.chartsRow}>
        <GeoMap
          title="Geographic Map (App Events)"
          rows={(data?.top_countries || []).map((c) => ({ code: c.country_code, name: c.country, value: c.views }))}
          valueLabel="Views"
          emptyHint="No geo data yet — countries appear once PLAYBACK_START events carry a country."
        />
      </div>

      <div className={styled.chartsRow}>
        <RankedList
          title="Top Countries (App Events)"
          rows={(data?.top_countries || []).slice(0, TOP_N).map((c) => ({ label: c.country, value: c.views }))}
          accent={COPPER}
          emptyHint="No geo data yet — appears once PLAYBACK_START events carry a country."
        />
        <RankedList
          title="Top Cities"
          rows={(data?.top_cities || []).slice(0, TOP_N).map((c) => ({ label: `${c.city}, ${c.country}`, value: c.views }))}
          accent={TEAL}
          emptyHint="No city-level data yet."
        />
      </div>

      <div className={styled.chartsRow}>
        {data?.bunny?.available ? (
          <LeaderboardTable
            title="Top Countries (Bunny CDN, library-wide)"
            rows={bunnyCountries.sort((a, b) => b.views - a.views).slice(0, TOP_N)}
            columns={[{ key: "country", label: "Country" }, { key: "views", label: "Views", align: "right" }]}
            emptyHint="No Bunny CDN geo data available."
          />
        ) : (
          <NotAvailableYet title="Bunny CDN Geography" reason={data?.bunny?.reason || "Bunny statistics unavailable."} />
        )}
      </div>
    </>
  );
};

export default GeoSection;
